import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Settings as SettingsIcon, Package, Check, CalendarOff, FileText, Upload, Plus, Trash2, Film, Image as ImageIcon, ChevronUp, ChevronDown, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { nl } from "date-fns/locale";
import { format } from "date-fns";
import api from "@/lib/api";
import { toast } from "sonner";
import VideoShotPresetsManager from "@/components/VideoShotPresetsManager";
import AiPhotoPresetsManager from "@/components/AiPhotoPresetsManager";

export default function AdminSettings() {
    const [settings, setSettings] = useState(null);
    const [saving, setSaving] = useState(false);
    const [products, setProducts] = useState([]);
    const [editingProduct, setEditingProduct] = useState(null);
    const [productForm, setProductForm] = useState(null);
    const [savingProduct, setSavingProduct] = useState(false);
    const [blockedDates, setBlockedDates] = useState([]); // Date[]
    const [savingBlocked, setSavingBlocked] = useState(false);
    const [creatingProduct, setCreatingProduct] = useState(false); // New product modal
    const [thumbUploading, setThumbUploading] = useState(false);
    const [exampleUploading, setExampleUploading] = useState(false);

    const uploadExampleVideo = async (file) => {
        if (!file) return;
        if (!file.type?.startsWith("video/")) {
            toast.error("Alleen video's toegestaan");
            return;
        }
        setExampleUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setProductForm((f) => ({ ...f, example_videos: [...(f.example_videos || []), data.url] }));
            toast.success("Voorbeeldvideo geüpload");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload mislukt");
        }
        setExampleUploading(false);
    };

    const load = async () => {
        const [s, p, b] = await Promise.all([
            api.get("/admin/settings"),
            api.get("/products", { params: { include_hidden: true } }),
            api.get("/availability/blocked"),
        ]);
        setSettings(s.data);
        setProducts(p.data);
        setBlockedDates((b.data || []).map((d) => new Date(d.date + "T00:00:00")));
    };

    useEffect(() => { load(); }, []);

    const saveSettings = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch("/admin/settings", settings);
            setSettings(data);
            toast.success("Instellingen opgeslagen");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fout");
        }
        setSaving(false);
    };

    const openProduct = (p) => {
        setEditingProduct(p.id);
        setProductForm({
            ...p,
            options: p.options || [],
            photo_tiers: p.photo_tiers || [],
        });
    };

    const saveProduct = async () => {
        setSavingProduct(true);
        try {
            const payload = {
                name: productForm.name,
                tagline: productForm.tagline,
                description: productForm.description,
                price: parseFloat(productForm.price),
                deliverables: productForm.deliverables,
                duration: productForm.duration,
                delivery_time: productForm.delivery_time,
                workflow: productForm.workflow,
                options: productForm.options,
                featured: productForm.featured,
                ai_badge: !!productForm.ai_badge,
                example_videos: productForm.example_videos || [],
                min_photos: productForm.min_photos,
                max_photos: productForm.max_photos,
                photo_tiers: productForm.photo_tiers,
                requires_on_site: productForm.requires_on_site,
                thumbnail_url: productForm.thumbnail_url || null,
                thumbnail_type: productForm.thumbnail_type || null,
            };
            if (creatingProduct) {
                payload.id = productForm.id?.trim() || undefined;
                payload.price_from = !!productForm.price_from;
                payload.order = parseInt(productForm.order) || 99;
                await api.post("/admin/products", payload);
                toast.success("Product aangemaakt");
            } else {
                await api.post(`/admin/products/${editingProduct}`, payload);
                toast.success("Product bijgewerkt");
            }
            setEditingProduct(null);
            setProductForm(null);
            setCreatingProduct(false);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fout");
        }
        setSavingProduct(false);
    };

    const openNewProduct = () => {
        setCreatingProduct(true);
        setEditingProduct("__new__");
        setProductForm({
            id: "",
            name: "",
            tagline: "",
            description: "",
            price: 0,
            price_from: false,
            deliverables: [],
            duration: "",
            delivery_time: "",
            workflow: "",
            options: [],
            featured: false,
            ai_badge: false,
            example_videos: [],
            photo_tiers: [],
            order: 99,
            thumbnail_url: "",
            thumbnail_type: null,
        });
    };

    const moveProduct = async (pid, dir) => {
        const ids = products.map((p) => p.id);
        const i = ids.indexOf(pid);
        const j = i + dir;
        if (j < 0 || j >= ids.length) return;
        [ids[i], ids[j]] = [ids[j], ids[i]];
        setProducts(ids.map((id) => products.find((p) => p.id === id)));
        try {
            await api.post("/admin/products/_reorder", { ids });
        } catch (e) {
            toast.error("Volgorde opslaan mislukt");
            load();
        }
    };

    const toggleHidden = async (p) => {
        try {
            await api.post(`/admin/products/${p.id}`, { hidden: !p.hidden });
            setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, hidden: !p.hidden } : x)));
            toast.success(!p.hidden ? `"${p.name}" verborgen uit de catalogus` : `"${p.name}" is weer zichtbaar`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fout");
        }
    };

    const deleteProduct = async (pid, pname) => {
        if (!window.confirm(`Product "${pname}" definitief verwijderen uit de catalogus?`)) return;
        try {
            await api.delete(`/admin/products/${pid}`);
            toast.success("Product verwijderd");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Verwijderen mislukt");
        }
    };

    const uploadThumbnail = async (file) => {
        if (!file) return;
        const isVideo = file.type?.startsWith("video/");
        const isImage = file.type?.startsWith("image/");
        if (!isVideo && !isImage) {
            toast.error("Alleen afbeeldingen of video's toegestaan");
            return;
        }
        setThumbUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setProductForm((f) => ({ ...f, thumbnail_url: data.url, thumbnail_type: isVideo ? "video" : "image" }));
            toast.success(`${isVideo ? "Video" : "Afbeelding"} geüpload`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload mislukt");
        }
        setThumbUploading(false);
    };

    const saveBlocked = async () => {
        setSavingBlocked(true);
        try {
            const dates = blockedDates.map((d) => format(d, "yyyy-MM-dd"));
            await api.post("/admin/blocked-dates", { dates });
            toast.success(`${dates.length} dagen geblokkeerd`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fout");
        }
        setSavingBlocked(false);
    };

    if (!settings) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center text-zinc-500">Laden...</div>;

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-zinc-950 font-body pb-20" data-testid="admin-settings-page">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-10">
                <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-950 mb-6" data-testid="admin-settings-back">
                    <ArrowLeft className="h-4 w-4" /> Terug
                </Link>
                <div className="mb-10">
                    <div className="text-xs uppercase tracking-[0.3em] text-[#E5A93D] font-semibold mb-3">Admin · Configuratie</div>
                    <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">Instellingen & producten.</h1>
                </div>

                {/* General settings */}
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8 mb-8" data-testid="general-settings">
                    <div className="flex items-center gap-2 mb-6">
                        <SettingsIcon className="h-4 w-4 text-[#E5A93D]" />
                        <h2 className="font-display font-bold text-xl tracking-tight">Verplaatsingskost</h2>
                    </div>
                    <div className="space-y-5">
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Basisadres (vertrekpunt voor verplaatsing)</Label>
                            <Input value={settings.base_address || ""} onChange={(e) => setSettings({ ...settings, base_address: e.target.value })} placeholder="bv. Markt 10, 8500 Kortrijk" className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D] h-11" data-testid="base-address-input" />
                            <p className="text-[11px] text-zinc-500 mt-1.5">Gebruikt voor automatische kilometer-berekening via OpenStreetMap.</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Tarief per km (€)</Label>
                                <Input type="number" step="0.01" value={settings.travel_rate_per_km} onChange={(e) => setSettings({ ...settings, travel_rate_per_km: parseFloat(e.target.value) || 0 })} className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D] h-11" data-testid="rate-input" />
                            </div>
                            <div className="flex items-end gap-3 pb-2">
                                <Switch checked={settings.round_trip} onCheckedChange={(v) => setSettings({ ...settings, round_trip: v })} data-testid="round-trip-switch" />
                                <div>
                                    <div className="text-sm text-zinc-950">Retourrit</div>
                                    <div className="text-[11px] text-zinc-500">Reken heen + terug</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <Button onClick={saveSettings} disabled={saving} className="mt-6 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm active:scale-[0.98]" data-testid="save-settings-btn">
                        {saving ? "Opslaan..." : "Instellingen opslaan"}
                    </Button>
                </motion.div>

                {/* Invoice / company details */}
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8 mb-8" data-testid="invoice-settings">
                    <div className="flex items-center gap-2 mb-2">
                        <FileText className="h-4 w-4 text-[#E5A93D]" />
                        <h2 className="font-display font-bold text-xl tracking-tight">Factuurgegevens</h2>
                    </div>
                    <p className="text-sm text-zinc-500 mb-6">
                        Deze gegevens verschijnen op alle PDF facturen die je voor opgeleverde projecten genereert.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Bedrijfsnaam</Label>
                            <Input value={settings.company_name || ""} onChange={(e) => setSettings({ ...settings, company_name: e.target.value })} className="bg-[#F4F4F5] border-zinc-200 rounded-sm h-11 focus-visible:border-[#E5A93D]" data-testid="inv-company-name" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">BTW-nummer</Label>
                            <Input value={settings.btw_number || ""} onChange={(e) => setSettings({ ...settings, btw_number: e.target.value })} placeholder="BE0123.456.789" className="bg-[#F4F4F5] border-zinc-200 rounded-sm h-11 focus-visible:border-[#E5A93D]" data-testid="inv-btw" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Adres (straat + nr)</Label>
                            <Input value={settings.company_address || ""} onChange={(e) => setSettings({ ...settings, company_address: e.target.value })} className="bg-[#F4F4F5] border-zinc-200 rounded-sm h-11 focus-visible:border-[#E5A93D]" data-testid="inv-address" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Postcode + gemeente</Label>
                            <Input value={settings.company_zip_city || ""} onChange={(e) => setSettings({ ...settings, company_zip_city: e.target.value })} placeholder="bv. 8500 Kortrijk" className="bg-[#F4F4F5] border-zinc-200 rounded-sm h-11 focus-visible:border-[#E5A93D]" data-testid="inv-zip-city" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">E-mail (op factuur)</Label>
                            <Input type="email" value={settings.company_email || ""} onChange={(e) => setSettings({ ...settings, company_email: e.target.value })} className="bg-[#F4F4F5] border-zinc-200 rounded-sm h-11 focus-visible:border-[#E5A93D]" data-testid="inv-email" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Telefoon (op factuur)</Label>
                            <Input value={settings.company_phone || ""} onChange={(e) => setSettings({ ...settings, company_phone: e.target.value })} className="bg-[#F4F4F5] border-zinc-200 rounded-sm h-11 focus-visible:border-[#E5A93D]" data-testid="inv-phone" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">IBAN</Label>
                            <Input value={settings.iban || ""} onChange={(e) => setSettings({ ...settings, iban: e.target.value })} placeholder="BE12 3456 7890 1234" className="bg-[#F4F4F5] border-zinc-200 rounded-sm h-11 focus-visible:border-[#E5A93D]" data-testid="inv-iban" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">BIC</Label>
                            <Input value={settings.bic || ""} onChange={(e) => setSettings({ ...settings, bic: e.target.value })} placeholder="GEBABEBB" className="bg-[#F4F4F5] border-zinc-200 rounded-sm h-11 focus-visible:border-[#E5A93D]" data-testid="inv-bic" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Factuurprefix</Label>
                            <Input value={settings.invoice_prefix || ""} onChange={(e) => setSettings({ ...settings, invoice_prefix: e.target.value.toUpperCase() })} placeholder="VSF" className="bg-[#F4F4F5] border-zinc-200 rounded-sm h-11 focus-visible:border-[#E5A93D]" data-testid="inv-prefix" />
                            <p className="text-[11px] text-zinc-500 mt-1.5">bv. VSF → factuurnummers worden VSF-2026-XXXXXXXX</p>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Betalingstermijn (dagen)</Label>
                            <Input type="number" min="0" max="90" value={settings.payment_terms_days ?? 14} onChange={(e) => setSettings({ ...settings, payment_terms_days: parseInt(e.target.value) || 0 })} className="bg-[#F4F4F5] border-zinc-200 rounded-sm h-11 focus-visible:border-[#E5A93D]" data-testid="inv-payment-terms" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">BTW-tarief (%)</Label>
                            <Input type="number" step="0.1" min="0" max="100" value={settings.vat_rate ?? 21} onChange={(e) => setSettings({ ...settings, vat_rate: parseFloat(e.target.value) || 0 })} className="bg-[#F4F4F5] border-zinc-200 rounded-sm h-11 focus-visible:border-[#E5A93D]" data-testid="inv-vat-rate" />
                        </div>
                        <div className="md:col-span-2 flex items-start gap-3 p-4 bg-[#F4F4F5] border border-zinc-200 rounded-sm">
                            <Switch checked={settings.prices_include_vat ?? true} onCheckedChange={(v) => setSettings({ ...settings, prices_include_vat: v })} data-testid="inv-prices-include-vat" />
                            <div>
                                <div className="text-sm text-zinc-950">Productprijzen zijn inclusief BTW</div>
                                <div className="text-[11px] text-zinc-500 leading-relaxed mt-0.5">
                                    Aan: factuur verdeelt het totaalbedrag tussen excl. BTW + BTW (eindbedrag = wat de klant zag in zijn dashboard).<br/>
                                    Uit: factuur telt 21% BTW bovenop de getoonde prijzen (klassieke B2B).
                                </div>
                            </div>
                        </div>
                        <div className="md:col-span-2">
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Factuurvoettekst (optioneel)</Label>
                            <Input value={settings.invoice_footer || ""} onChange={(e) => setSettings({ ...settings, invoice_footer: e.target.value })} placeholder="bv. Bedankt voor uw vertrouwen — Vastgoed Social Factory" className="bg-[#F4F4F5] border-zinc-200 rounded-sm h-11 focus-visible:border-[#E5A93D]" data-testid="inv-footer" />
                        </div>
                    </div>
                    <Button onClick={saveSettings} disabled={saving} className="mt-6 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm active:scale-[0.98]" data-testid="save-invoice-settings-btn">
                        {saving ? "Opslaan..." : "Factuurgegevens opslaan"}
                    </Button>
                </motion.div>

                {/* Blocked dates */}
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8 mb-8" data-testid="blocked-dates-section">
                    <div className="flex items-center gap-2 mb-2">
                        <CalendarOff className="h-4 w-4 text-red-600" />
                        <h2 className="font-display font-bold text-xl tracking-tight">Geblokkeerde data</h2>
                    </div>
                    <p className="text-sm text-zinc-500 mb-6">
                        Klik op een datum om te blokkeren. Makelaars kunnen deze dagen niet kiezen als voorkeursmoment voor opnames. <strong className="text-zinc-950">{blockedDates.length}</strong> dagen momenteel geblokkeerd.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-start">
                        <div className="bg-[#F4F4F5] border border-zinc-200 rounded-md p-3">
                            <Calendar
                                mode="multiple"
                                selected={blockedDates}
                                onSelect={(dates) => setBlockedDates(dates || [])}
                                locale={nl}
                                className="rdp-admin"
                                modifiersClassNames={{ selected: "bg-red-500 text-zinc-950 hover:bg-red-400" }}
                                data-testid="blocked-calendar"
                            />
                        </div>
                        <div className="space-y-3">
                            <div className="text-xs uppercase tracking-wider text-zinc-500">Geselecteerd</div>
                            {blockedDates.length === 0 ? (
                                <div className="text-sm text-zinc-500">Geen geblokkeerde data</div>
                            ) : (
                                <div className="flex flex-wrap gap-2 max-h-40 overflow-auto scrollbar-thin">
                                    {[...blockedDates].sort((a, b) => a - b).map((d) => (
                                        <span key={d.toISOString()} className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/10 border border-red-500/30 rounded-sm text-xs text-red-700">
                                            {format(d, "d MMM yyyy", { locale: nl })}
                                        </span>
                                    ))}
                                </div>
                            )}
                            <Button onClick={saveBlocked} disabled={savingBlocked} className="bg-red-500 hover:bg-red-400 text-zinc-950 font-bold rounded-sm active:scale-[0.98] mt-4" data-testid="save-blocked-btn">
                                {savingBlocked ? "Opslaan..." : "Blokkade opslaan"}
                            </Button>
                        </div>
                    </div>
                </motion.div>

                {/* Products */}
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8" data-testid="admin-products-section">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-2">
                            <Package className="h-4 w-4 text-[#E5A93D]" />
                            <h2 className="font-display font-bold text-xl tracking-tight">Producten & prijzen</h2>
                        </div>
                        <button
                            type="button"
                            onClick={openNewProduct}
                            className="inline-flex items-center gap-2 h-9 px-3 rounded-sm text-xs font-medium bg-[#E5A93D] text-black hover:bg-[#C8922C] active:scale-[0.98] transition"
                            data-testid="new-product-btn"
                        >
                            <Plus className="h-4 w-4" />
                            Nieuw product
                        </button>
                    </div>
                    <div className="space-y-3">
                        {products.map((p, idx) => (
                            <div key={p.id} className={`flex items-center gap-4 p-4 bg-[#F4F4F5] border border-zinc-200 rounded-sm hover:border-zinc-400 ${p.hidden ? "opacity-55" : ""}`} data-testid={`admin-product-row-${p.id}`}>
                                <div className="flex flex-col gap-0.5">
                                    <button onClick={() => moveProduct(p.id, -1)} disabled={idx === 0}
                                        className="h-6 w-6 flex items-center justify-center rounded-sm border border-zinc-200 bg-white text-zinc-500 hover:text-zinc-950 hover:border-zinc-400 disabled:opacity-30"
                                        aria-label="Omhoog" data-testid={`move-up-${p.id}`}>
                                        <ChevronUp className="h-3.5 w-3.5" />
                                    </button>
                                    <button onClick={() => moveProduct(p.id, 1)} disabled={idx === products.length - 1}
                                        className="h-6 w-6 flex items-center justify-center rounded-sm border border-zinc-200 bg-white text-zinc-500 hover:text-zinc-950 hover:border-zinc-400 disabled:opacity-30"
                                        aria-label="Omlaag" data-testid={`move-down-${p.id}`}>
                                        <ChevronDown className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                                <div className="h-14 w-20 flex-shrink-0 bg-zinc-50 rounded-sm overflow-hidden border border-zinc-200 flex items-center justify-center">
                                    {p.thumbnail_url && p.thumbnail_type === "video" ? (
                                        <video src={p.thumbnail_url} muted playsInline preload="metadata" className="h-full w-full object-cover" />
                                    ) : p.thumbnail_url ? (
                                        <img src={p.thumbnail_url} alt="" className="h-full w-full object-cover" />
                                    ) : (
                                        <Package className="h-5 w-5 text-zinc-400" />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2">
                                        {p.name}
                                        {p.hidden && <span className="px-1.5 py-0.5 bg-zinc-200 text-zinc-600 rounded-sm normal-case tracking-normal font-bold">Verborgen</span>}
                                    </div>
                                    <div className="text-zinc-950 font-medium truncate">{p.tagline}</div>
                                    <div className="text-xs text-zinc-500 mt-1 flex items-center gap-3 flex-wrap">
                                        {p.price_from && <span>vanaf</span>}
                                        <span className="text-[#E5A93D] font-bold">€{p.price}</span>
                                        {p.requires_on_site && <span className="text-blue-600">+ verplaatsing</span>}
                                        {p.photo_tiers?.length > 0 && <span>{p.photo_tiers.length} prijs-tiers</span>}
                                    </div>
                                </div>
                                <button onClick={() => toggleHidden(p)}
                                    className={`h-9 px-3 flex items-center gap-1.5 text-sm rounded-sm border ${p.hidden ? "border-[#E5A93D]/50 text-[#C8922C] hover:bg-[#E5A93D]/10" : "border-zinc-200 bg-zinc-100 text-zinc-600 hover:border-zinc-400"}`}
                                    data-testid={`toggle-hidden-${p.id}`}>
                                    {p.hidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                                    {p.hidden ? "Tonen" : "Verbergen"}
                                </button>
                                <button onClick={() => openProduct(p)} className="px-3 py-1.5 text-sm bg-zinc-100 hover:bg-zinc-100 border border-zinc-200 rounded-sm" data-testid={`edit-product-${p.id}`}>
                                    Bewerken
                                </button>
                                <button
                                    onClick={() => deleteProduct(p.id, p.name)}
                                    className="h-9 w-9 flex items-center justify-center rounded-sm border border-red-500/30 text-red-600 hover:bg-red-500/10"
                                    aria-label="Verwijderen"
                                    data-testid={`delete-product-${p.id}`}
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                </motion.div>

                {/* Video shot presets manager */}
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                    <VideoShotPresetsManager />
                </motion.div>

                {/* AI photo presets manager */}
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                    <AiPhotoPresetsManager />
                </motion.div>

                {/* Product editor modal */}
                {editingProduct && productForm && (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-auto" onClick={() => { setEditingProduct(null); setCreatingProduct(false); }}>
                        <div className="bg-[#FFFFFF] border border-zinc-200 rounded-md max-w-2xl w-full max-h-[90vh] overflow-auto scrollbar-thin" onClick={(e) => e.stopPropagation()} data-testid="product-edit-modal">
                            <div className="p-6 lg:p-8">
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="font-display font-bold text-2xl">
                                        {creatingProduct ? "Nieuw product toevoegen" : `Bewerk ${productForm.name}`}
                                    </h3>
                                    <button onClick={() => { setEditingProduct(null); setCreatingProduct(false); }} className="text-zinc-500 hover:text-zinc-950">×</button>
                                </div>
                                <div className="space-y-4">
                                    {creatingProduct && (
                                        <TextField
                                            label="Product-id (optioneel — auto-gegenereerd uit naam)"
                                            value={productForm.id}
                                            onChange={(v) => setProductForm({ ...productForm, id: v.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
                                            testid="pf-id"
                                        />
                                    )}
                                    <TextField label="Naam" value={productForm.name} onChange={(v) => setProductForm({ ...productForm, name: v })} testid="pf-name" />
                                    <TextField label="Tagline" value={productForm.tagline} onChange={(v) => setProductForm({ ...productForm, tagline: v })} testid="pf-tagline" />
                                    <TextField label="Beschrijving" value={productForm.description} onChange={(v) => setProductForm({ ...productForm, description: v })} testid="pf-description" textarea />

                                    {/* Thumbnail uploader */}
                                    <div className="p-4 bg-[#F4F4F5] border border-zinc-200 rounded-sm" data-testid="thumbnail-block">
                                        <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-3 block">
                                            Thumbnail (video of afbeelding)
                                        </Label>
                                        <div className="flex items-start gap-4">
                                            <div className="h-28 w-44 flex-shrink-0 bg-zinc-50 rounded-sm overflow-hidden border border-zinc-200 flex items-center justify-center">
                                                {productForm.thumbnail_url && productForm.thumbnail_type === "video" ? (
                                                    <video src={productForm.thumbnail_url} muted loop autoPlay playsInline className="h-full w-full object-cover" data-testid="thumb-preview-video" />
                                                ) : productForm.thumbnail_url ? (
                                                    <img src={productForm.thumbnail_url} alt="" className="h-full w-full object-cover" data-testid="thumb-preview-image" />
                                                ) : (
                                                    <div className="flex flex-col items-center gap-1 text-zinc-400">
                                                        <Film className="h-6 w-6" />
                                                        <span className="text-[10px] uppercase tracking-wider">Geen</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex-1 space-y-2">
                                                <label className="inline-flex items-center gap-2 px-3 py-2 bg-[#FFFFFF] hover:bg-[#F4F4F5] border border-zinc-200 rounded-sm cursor-pointer transition-colors text-sm" data-testid="thumb-upload-btn">
                                                    <Upload className="h-4 w-4" />
                                                    <span>{thumbUploading ? "Uploaden…" : productForm.thumbnail_url ? "Thumbnail vervangen" : "Thumbnail uploaden"}</span>
                                                    <input
                                                        type="file"
                                                        accept="image/*,video/*"
                                                        onChange={(e) => uploadThumbnail(e.target.files?.[0])}
                                                        className="hidden"
                                                        disabled={thumbUploading}
                                                        data-testid="thumb-upload-input"
                                                    />
                                                </label>
                                                <p className="text-[11px] text-zinc-500 leading-relaxed">
                                                    Aanbevolen: MP4/M4V/MOV video of JPG/PNG afbeelding. Wordt op de landingspagina getoond in de productkaart.
                                                </p>
                                                {productForm.thumbnail_url && (
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[10px] uppercase tracking-wider text-[#E5A93D] flex items-center gap-1">
                                                            {productForm.thumbnail_type === "video" ? <Film className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
                                                            {productForm.thumbnail_type}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() => setProductForm({ ...productForm, thumbnail_url: "", thumbnail_type: null })}
                                                            className="text-xs text-red-600 hover:underline"
                                                            data-testid="thumb-remove-btn"
                                                        >
                                                            Verwijderen
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <TextField label={productForm.price_from ? "Basisprijs (vanaf €)" : "Prijs (€)"} value={productForm.price} onChange={(v) => setProductForm({ ...productForm, price: v })} testid="pf-price" type="number" step="0.01" />
                                    <TextField label="Duurtijd op locatie" value={productForm.duration || ""} onChange={(v) => setProductForm({ ...productForm, duration: v })} testid="pf-duration" />
                                    <TextField label="Oplevering" value={productForm.delivery_time || ""} onChange={(v) => setProductForm({ ...productForm, delivery_time: v })} testid="pf-delivery-time" />
                                    <TextField label="Werkwijze" value={productForm.workflow || ""} onChange={(v) => setProductForm({ ...productForm, workflow: v })} testid="pf-workflow" textarea />
                                    <div className="flex items-center gap-3">
                                        <Switch checked={!!productForm.requires_on_site} onCheckedChange={(v) => setProductForm({ ...productForm, requires_on_site: v })} data-testid="pf-onsite-switch" />
                                        <span className="text-sm text-zinc-600">Vereist opname op locatie (verplaatsing wordt aangerekend)</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Switch checked={!!productForm.featured} onCheckedChange={(v) => setProductForm({ ...productForm, featured: v })} data-testid="pf-featured-switch" />
                                        <span className="text-sm text-zinc-600">Featured / Premium badge</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Switch checked={!!productForm.ai_badge} onCheckedChange={(v) => setProductForm({ ...productForm, ai_badge: v })} data-testid="pf-ai-badge-switch" />
                                        <span className="text-sm text-zinc-600">Blauwe <span className="inline-block bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-sm align-middle">AI</span> badge op de cataloguskaart</span>
                                    </div>
                                    <div className="p-4 bg-[#F4F4F5] border border-zinc-200 rounded-sm" data-testid="example-videos-block">
                                        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Voorbeeldvideo's (in de info-popup)</div>
                                        <p className="text-xs text-zinc-500 mb-3">Deze video's worden getoond wanneer een bezoeker op de productkaart klikt voor meer info.</p>
                                        {(productForm.example_videos || []).length > 0 && (
                                            <div className="grid grid-cols-2 gap-3 mb-3">
                                                {productForm.example_videos.map((url, vi) => (
                                                    <div key={vi} className="relative group/vid">
                                                        <video src={url} muted playsInline preload="metadata" controls className="w-full h-28 object-cover rounded-sm border border-zinc-200 bg-black" />
                                                        <button onClick={() => setProductForm((f) => ({ ...f, example_videos: f.example_videos.filter((_, j) => j !== vi) }))}
                                                            className="absolute top-1.5 right-1.5 h-6 w-6 flex items-center justify-center rounded-sm bg-black/70 text-white hover:bg-red-600"
                                                            data-testid={`remove-example-video-${vi}`}>
                                                            <Trash2 className="h-3 w-3" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <label className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-white border border-zinc-200 rounded-sm cursor-pointer hover:border-zinc-400" data-testid="upload-example-video">
                                            <Upload className="h-4 w-4" />
                                            {exampleUploading ? "Bezig met uploaden…" : "Video toevoegen"}
                                            <input type="file" accept="video/*" className="hidden" onChange={(e) => { uploadExampleVideo(e.target.files?.[0]); e.target.value = ""; }} />
                                        </label>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Switch checked={!!productForm.price_from} onCheckedChange={(v) => setProductForm({ ...productForm, price_from: v })} data-testid="pf-price-from-switch" />
                                        <span className="text-sm text-zinc-600">"Vanaf"-prijs (toont vanaf € op de kaart)</span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <TextField label="Min. foto's (AI upload)" value={productForm.min_photos ?? ""} onChange={(v) => setProductForm({ ...productForm, min_photos: v === "" ? null : parseInt(v) || 0 })} testid="pf-min-photos" type="number" />
                                        <TextField label="Max. foto's (AI upload)" value={productForm.max_photos ?? ""} onChange={(v) => setProductForm({ ...productForm, max_photos: v === "" ? null : parseInt(v) || 0 })} testid="pf-max-photos" type="number" />
                                    </div>

                                    {/* Deliverables (checkmark bullets on the product card) */}
                                    <div className="p-4 bg-[#F4F4F5] border border-zinc-200 rounded-sm" data-testid="deliverables-block">
                                        <div className="flex items-center justify-between mb-3">
                                            <Label className="text-xs uppercase tracking-wider text-zinc-500">Kenmerken (vinkjes op de productkaart)</Label>
                                            <button
                                                type="button"
                                                onClick={() => setProductForm({ ...productForm, deliverables: [...(productForm.deliverables || []), ""] })}
                                                className="inline-flex items-center gap-1 text-xs text-[#E5A93D] hover:text-[#C8922C] font-medium"
                                                data-testid="add-deliverable-btn"
                                            >
                                                + Kenmerk toevoegen
                                            </button>
                                        </div>
                                        {(productForm.deliverables || []).length > 0 ? (
                                            <div className="space-y-2">
                                                {productForm.deliverables.map((d, i) => (
                                                    <div key={i} className="grid grid-cols-[1fr_40px] gap-2">
                                                        <Input value={d} onChange={(e) => {
                                                            const ds = [...productForm.deliverables];
                                                            ds[i] = e.target.value;
                                                            setProductForm({ ...productForm, deliverables: ds });
                                                        }} placeholder="bv. Reel of video 25-45 sec" className="bg-[#FFFFFF] border-zinc-200 rounded-sm h-10" data-testid={`deliverable-${i}-input`} />
                                                        <button
                                                            type="button"
                                                            onClick={() => setProductForm({ ...productForm, deliverables: productForm.deliverables.filter((_, x) => x !== i) })}
                                                            className="h-10 border border-zinc-200 rounded-sm text-red-500 hover:bg-red-50"
                                                            data-testid={`deliverable-${i}-remove`}
                                                        >×</button>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-xs text-zinc-400">Nog geen kenmerken — voeg er toe met de knop hierboven.</p>
                                        )}
                                    </div>

                                    {/* Photo tiers */}
                                    {productForm.photo_tiers && productForm.photo_tiers.length > 0 && (
                                        <div className="p-4 bg-[#F4F4F5] border border-zinc-200 rounded-sm">
                                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-3 block">Prijstiers per foto-aantal</Label>
                                            <div className="space-y-2">
                                                {productForm.photo_tiers.map((t, i) => (
                                                    <div key={i} className="grid grid-cols-4 gap-2">
                                                        <Input type="number" value={t.min_photos} onChange={(e) => {
                                                            const tiers = [...productForm.photo_tiers];
                                                            tiers[i] = { ...tiers[i], min_photos: parseInt(e.target.value) || 0 };
                                                            setProductForm({ ...productForm, photo_tiers: tiers });
                                                        }} placeholder="Min" className="bg-[#FFFFFF] border-zinc-200 rounded-sm h-10" data-testid={`tier-${i}-min`} />
                                                        <Input type="number" value={t.max_photos} onChange={(e) => {
                                                            const tiers = [...productForm.photo_tiers];
                                                            tiers[i] = { ...tiers[i], max_photos: parseInt(e.target.value) || 0 };
                                                            setProductForm({ ...productForm, photo_tiers: tiers });
                                                        }} placeholder="Max" className="bg-[#FFFFFF] border-zinc-200 rounded-sm h-10" data-testid={`tier-${i}-max`} />
                                                        <Input type="number" step="0.01" value={t.price} onChange={(e) => {
                                                            const tiers = [...productForm.photo_tiers];
                                                            tiers[i] = { ...tiers[i], price: parseFloat(e.target.value) || 0 };
                                                            setProductForm({ ...productForm, photo_tiers: tiers });
                                                        }} placeholder="Prijs" className="bg-[#FFFFFF] border-zinc-200 rounded-sm h-10" data-testid={`tier-${i}-price`} />
                                                        <Input value={t.label} onChange={(e) => {
                                                            const tiers = [...productForm.photo_tiers];
                                                            tiers[i] = { ...tiers[i], label: e.target.value };
                                                            setProductForm({ ...productForm, photo_tiers: tiers });
                                                        }} placeholder="Label" className="bg-[#FFFFFF] border-zinc-200 rounded-sm h-10" data-testid={`tier-${i}-label`} />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Options */}
                                    <div className="p-4 bg-[#F4F4F5] border border-zinc-200 rounded-sm" data-testid="options-block">
                                        <div className="flex items-center justify-between mb-3">
                                            <Label className="text-xs uppercase tracking-wider text-zinc-500">Extra opties</Label>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const opts = [...(productForm.options || [])];
                                                    opts.push({ id: `opt_${Date.now()}`, label: "", price: 0 });
                                                    setProductForm({ ...productForm, options: opts });
                                                }}
                                                className="inline-flex items-center gap-1 text-xs text-[#E5A93D] hover:text-[#C8922C] font-medium"
                                                data-testid="add-option-btn"
                                            >
                                                + Optie toevoegen
                                            </button>
                                        </div>
                                        {productForm.options && productForm.options.length > 0 ? (
                                            <div className="space-y-2">
                                                <div className="grid grid-cols-[1fr_120px_40px] gap-2 text-[10px] uppercase tracking-wider text-zinc-500 px-1">
                                                    <span>Label</span>
                                                    <span>Prijs (€)</span>
                                                    <span></span>
                                                </div>
                                                {productForm.options.map((o, i) => (
                                                    <div key={o.id || i} className="grid grid-cols-[1fr_120px_40px] gap-2 items-center">
                                                        <Input value={o.label} placeholder="bv. Extra drone shot" onChange={(e) => {
                                                            const opts = [...productForm.options];
                                                            opts[i] = { ...opts[i], label: e.target.value };
                                                            setProductForm({ ...productForm, options: opts });
                                                        }} className="bg-[#FFFFFF] border-zinc-200 rounded-sm h-10" data-testid={`opt-${i}-label`} />
                                                        <Input type="number" step="0.01" value={o.price} onChange={(e) => {
                                                            const opts = [...productForm.options];
                                                            opts[i] = { ...opts[i], price: parseFloat(e.target.value) || 0 };
                                                            setProductForm({ ...productForm, options: opts });
                                                        }} className="bg-[#FFFFFF] border-zinc-200 rounded-sm h-10" data-testid={`opt-${i}-price`} />
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const opts = productForm.options.filter((_, idx) => idx !== i);
                                                                setProductForm({ ...productForm, options: opts });
                                                            }}
                                                            className="h-10 rounded-sm border border-red-500/30 text-red-600 hover:bg-red-500/10 flex items-center justify-center"
                                                            aria-label="Optie verwijderen"
                                                            data-testid={`opt-${i}-remove`}
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-xs text-zinc-500 italic">Nog geen extra opties toegevoegd.</p>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 mt-6">
                                    <Button onClick={saveProduct} disabled={savingProduct} className="bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm active:scale-[0.98]" data-testid="save-product-btn">
                                        {savingProduct ? "Opslaan..." : (creatingProduct ? "Product aanmaken" : "Product opslaan")}
                                    </Button>
                                    <button onClick={() => { setEditingProduct(null); setCreatingProduct(false); }} className="text-sm text-zinc-500 hover:text-zinc-950">Annuleren</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function TextField({ label, value, onChange, testid, type = "text", step, textarea = false }) {
    return (
        <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">{label}</Label>
            {textarea ? (
                <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} className="w-full bg-[#F4F4F5] border border-zinc-200 rounded-sm p-3 text-sm text-zinc-800 focus:outline-none focus:border-[#E5A93D]" data-testid={testid} />
            ) : (
                <Input type={type} step={step} value={value} onChange={(e) => onChange(e.target.value)} className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D] h-11" data-testid={testid} />
            )}
        </div>
    );
}
